import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "./Brutal";
import { Handshake, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

/** v414.50 — per-vial override commissions, MULTIPLE rules per distributor.
 * e.g. a sister's sale pays Maryna $4/vial AND Olesya $1/vial — each rule
 * books its own pending payout on every sale; optional seller share comes off
 * the seller's payout. Vials only; accessories excluded. */
export function OverrideCommissionCard({ distributor, onChanged }) {
  const { t } = useTranslation();
  const rules = [
    ...(distributor.override_commissions || []),
    ...(distributor.override_commission ? [distributor.override_commission] : []),
  ].filter((r) => r && r.active);
  const [adding, setAdding] = useState(false);
  const [options, setOptions] = useState([]);
  const [beneficiaryId, setBeneficiaryId] = useState("");
  const [perUnit, setPerUnit] = useState("5");
  const [sellerShare, setSellerShare] = useState("0");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!adding) return;
    api.get("/distributors")
      .then((r) => setOptions((r.data || []).filter(
        (d) => d.id !== distributor.id && !d.is_quick && !d.is_personal)))
      .catch(() => {});
  }, [adding, distributor.id]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/distributors/${distributor.id}/override-commissions`, {
        beneficiary_id: beneficiaryId,
        per_unit: parseFloat(perUnit) || 0,
        seller_share: parseFloat(sellerShare) || 0,
      });
      toast.success(t("override.saved"));
      setAdding(false); setBeneficiaryId(""); setPerUnit("5"); setSellerShare("0");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(t("override.remove_confirm"))) return;
    try {
      await api.delete(`/distributors/${distributor.id}/override-commissions/${rule.beneficiary_id}`);
      toast.success(t("override.removed"));
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <BrutalCard data-testid="override-commission-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-semibold flex items-center gap-2">
            <Handshake size={18} weight="bold" className="text-[#8a6a1c]" />
            {t("override.title")}
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">{t("override.hint")}</p>
        </div>
        {!adding && (
          <BrutalButton size="sm" variant="secondary" onClick={() => setAdding(true)} data-testid="override-add-btn">
            <Plus size={14} weight="bold" /> {t("override.add_btn")}
          </BrutalButton>
        )}
      </div>

      {rules.length > 0 && (
        <div className="mt-3 space-y-2">
          {rules.map((r) => (
            <div key={r.beneficiary_id}
              className="flex items-center justify-between gap-2 border border-zinc-200 rounded-lg px-3 py-2"
              data-testid={`override-rule-${r.beneficiary_id}`}>
              <p className="text-sm min-w-0">
                <span className="font-semibold text-[#8a6a1c]">{r.beneficiary_name}</span>{" "}
                {t("override.summary", { per: Number(r.per_unit).toFixed(2), name: distributor.name })}
                {Number(r.seller_share) > 0 && (
                  <span className="text-zinc-500"> {t("override.share_note", {
                    share: Number(r.seller_share).toFixed(2),
                    house: (Number(r.per_unit) - Number(r.seller_share)).toFixed(2),
                  })}</span>
                )}
              </p>
              <button onClick={() => remove(r)} title={t("override.remove_title")}
                className="p-1.5 text-zinc-500 hover:text-red-600 shrink-0"
                data-testid={`override-remove-${r.beneficiary_id}`}>
                <Trash size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
      {rules.length === 0 && !adding && (
        <p className="text-sm text-zinc-500 mt-2" data-testid="override-empty">{t("override.empty")}</p>
      )}

      {adding && (
        <form onSubmit={save} className="mt-3 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-semibold mb-1">{t("override.beneficiary_label")}</label>
            <select
              value={beneficiaryId}
              onChange={(e) => setBeneficiaryId(e.target.value)}
              required
              className="w-full h-10 px-3 text-sm border-2 border-zinc-900 rounded-lg bg-white"
              data-testid="override-beneficiary-select"
            >
              <option value="">{t("override.pick_beneficiary")}</option>
              {options.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.distributor_code ? ` · ${d.distributor_code}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="w-28">
            <label className="block text-xs font-semibold mb-1">{t("override.per_unit_label")}</label>
            <BrutalInput type="number" step="0.01" min="0.01" value={perUnit}
              onChange={(e) => setPerUnit(e.target.value)} required data-testid="override-per-unit-input" />
          </div>
          <div className="w-28">
            <label className="block text-xs font-semibold mb-1">{t("override.seller_share_label")}</label>
            <BrutalInput type="number" step="0.01" min="0" value={sellerShare}
              onChange={(e) => setSellerShare(e.target.value)} data-testid="override-seller-share-input" />
          </div>
          <BrutalButton type="submit" size="sm" disabled={busy} data-testid="override-save-btn">
            {busy ? t("common.loading") : t("common.save")}
          </BrutalButton>
          <BrutalButton type="button" size="sm" variant="secondary" onClick={() => setAdding(false)} data-testid="override-cancel-btn">
            {t("common.cancel")}
          </BrutalButton>
        </form>
      )}
    </BrutalCard>
  );
}
